import { useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

/** AI-inferred standards-based defaults for parameters the client document never stated.
 *  Nothing is written until the engineer accepts it. */
export default function InferPanel({ projectId, setSpecs }) {
  const [busy, setBusy] = useState(false);
  const [sugg, setSugg] = useState(null);
  const [accepted, setAccepted] = useState({});

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/autofill/infer`);
      setSugg(data.suggestions);
      setAccepted({});
      const n = Object.keys(data.suggestions || {}).length;
      toast.success(n ? `${n} standards-based default${n > 1 ? "s" : ""} proposed` : "No gaps left to infer");
    } catch (e) { toast.error(fmtErr(e), { duration: 8000 }); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    const values = {}, provenance = {};
    Object.entries(sugg || {}).forEach(([fid, v]) => {
      if (accepted[fid]) { values[fid] = v.value; provenance[fid] = v; }
    });
    if (!Object.keys(values).length) { toast.error("Accept at least one default first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/autofill/apply`, { values, provenance });
      setSpecs((s) => ({ ...s, ...values }));
      setSugg((cur) => Object.fromEntries(Object.entries(cur).filter(([fid]) => !values[fid])));
      toast.success(`${data.applied} value(s) written into the specification`);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const rows = Object.entries(sugg || {});
  const count = rows.filter(([fid]) => accepted[fid]).length;

  return (
    <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] shadow-sm" data-testid="infer-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-[var(--c-border)]">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-[var(--c-text)]">
            <Sparkle size={13} className="text-[var(--c-accent)]" /> AI-inferred defaults
          </div>
          <div className="text-[11px] text-[var(--c-muted)] mt-0.5">
            For parameters the client documents never state — each default cites its standard and is written only
            after you accept it
          </div>
        </div>
        <div className="flex gap-2">
          {rows.length > 0 && (
            <Button size="sm" data-testid="infer-apply" onClick={apply} disabled={busy || !count}
              className="bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm h-7 text-[11px]">
              Accept {count || ""} selected
            </Button>
          )}
          <Button size="sm" variant="outline" data-testid="infer-run" onClick={run} disabled={busy}
            className="border-[var(--c-accent)]/50 text-[var(--c-accent)] hover:bg-[var(--c-accent)]/10 rounded-sm h-7 text-[11px]">
            <Sparkle size={12} className="mr-1" /> {busy ? "Working…" : rows.length ? "Re-run" : "Propose defaults"}
          </Button>
        </div>
      </div>
      {rows.length > 0 && (
        <div className="max-h-72 overflow-y-auto divide-y divide-[var(--c-border)]">
          {rows.map(([fid, v]) => (
            <div key={fid} className="flex items-start gap-3 px-4 py-2.5" data-testid={`infer-row-${fid}`}>
              <Switch data-testid={`infer-accept-${fid}`} checked={!!accepted[fid]}
                onCheckedChange={(on) => setAccepted((a) => ({ ...a, [fid]: on }))} className="mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-xs text-[var(--c-text)]">{v.label || fid}</span>
                  <span className="font-mono text-[11px] text-[var(--c-accent)]">
                    {Array.isArray(v.value) ? v.value.join("; ") : String(v.value)} {v.unit}
                  </span>
                  <span className="font-mono text-[10px] text-[var(--c-muted)]">{Math.round((v.confidence || 0) * 100)}%</span>
                </div>
                <div className="text-[10px] text-[var(--c-muted)] mt-0.5 leading-relaxed">
                  {v.basis && <span className="text-[var(--c-text)]">{v.basis}</span>}{v.reason ? ` — ${v.reason}` : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
